import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  LabelList
} from 'recharts';

// Helper function to calculate pairs from records using FIFO
const calculatePairsFromRecords = (records) => {
  const entriesBySymbol = {};
  const exitsBySymbol = {};

  const sortedRecords = [...records].sort((a, b) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  sortedRecords.forEach(record => {
    const symbol = record.symbol;
    if (record.action === 'entry') {
      if (!entriesBySymbol[symbol]) entriesBySymbol[symbol] = [];
      entriesBySymbol[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity,
        timestamp: record.timestamp
      });
    } else {
      if (!exitsBySymbol[symbol]) exitsBySymbol[symbol] = [];
      exitsBySymbol[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity,
        timestamp: record.timestamp
      });
    }
  });

  const pairs = [];
  Object.keys(entriesBySymbol).forEach(symbol => {
    const entries = [...entriesBySymbol[symbol]];
    const exits = exitsBySymbol[symbol] || [];

    exits.forEach(exit => {
      if (entries.length > 0) {
        const entry = entries.shift();
        const qty = Math.min(entry.quantity, exit.quantity);
        pairs.push({
          symbol,
          profit: (exit.price - entry.price) * qty
        });
      }
    });
  });

  return pairs;
};

/**
 * Win Rate Chart
 * Shows win rate percentage by stock symbol
 * Color gradient: Red (<50%), Yellow (50-70%), Green (>70%)
 */
const WinRateChart = ({ records, pairs = [] }) => {
  // Calculate win rate by symbol from pairs (with fallback to records)
  const winRateBySymbol = useMemo(() => {
    // Use provided pairs or calculate from records
    const pairsToUse = pairs && pairs.length > 0 ? pairs : calculatePairsFromRecords(records);

    const symbolStats = {};

    pairsToUse.forEach(pair => {
      const symbol = pair.symbol;
      if (!symbolStats[symbol]) {
        symbolStats[symbol] = {
          symbol,
          wins: 0,
          losses: 0,
          trades: 0,
          totalPnl: 0
        };
      }
      symbolStats[symbol].trades += 1;
      symbolStats[symbol].totalPnl += pair.profit || 0;
      if ((pair.profit || 0) > 0) {
        symbolStats[symbol].wins += 1;
      } else {
        symbolStats[symbol].losses += 1;
      }
    });

    // Calculate win rate and sort
    return Object.values(symbolStats)
      .map(stat => ({
        ...stat,
        winRate: stat.trades > 0 ? (stat.wins / stat.trades) * 100 : 0
      }))
      .sort((a, b) => b.winRate - a.winRate);
  }, [records, pairs]);

  // Get color based on win rate
  const getWinRateColor = (rate) => {
    if (rate >= 70) return '#22c55e'; // Green
    if (rate >= 50) return '#f59e0b'; // Amber/Yellow
    return '#ef4444'; // Red
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const color = getWinRateColor(data.winRate);

      return (
        <div style={{
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          minWidth: '150px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '15px' }}>
            {data.symbol}
          </div>
          <div style={{ fontSize: '13px' }}>
            <div style={{ color: color, fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>
              Win Rate: {data.winRate.toFixed(0)}%
            </div>
            <div style={{ color: '#666' }}>
              Trades: {data.trades} ({data.wins}W / {data.losses}L)
            </div>
            <div style={{
              color: data.totalPnl >= 0 ? '#22c55e' : '#ef4444',
              marginTop: '4px'
            }}>
              Total P&L: {data.totalPnl >= 0 ? '+' : ''}${data.totalPnl.toFixed(2)}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (winRateBySymbol.length === 0) {
    return (
      <div style={{
        height: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        No completed trades to display. Win Rate requires matched Entry/Exit pairs.
      </div>
    );
  }

  // Calculate overall stats
  const totalTrades = winRateBySymbol.reduce((sum, s) => sum + s.trades, 0);
  const totalWins = winRateBySymbol.reduce((sum, s) => sum + s.wins, 0);
  const overallWinRate = totalTrades > 0 ? (totalWins / totalTrades) * 100 : 0;

  return (
    <div style={{ width: '100%', height: Math.max(400, winRateBySymbol.length * 50 + 80) }}>
      {/* Summary */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '12px 16px',
        background: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <div>
          <span style={{ color: '#666', fontSize: '13px' }}>Total Stocks: </span>
          <span style={{ fontWeight: '600' }}>{winRateBySymbol.length}</span>
        </div>
        <div>
          <span style={{ color: '#666', fontSize: '13px' }}>Total Trades: </span>
          <span style={{ fontWeight: '600' }}>{totalTrades}</span>
        </div>
        <div>
          <span style={{ color: '#666', fontSize: '13px' }}>Overall Win Rate: </span>
          <span style={{
            fontWeight: 'bold',
            fontSize: '20px',
            color: getWinRateColor(overallWinRate)
          }}>
            {overallWinRate.toFixed(0)}%
          </span>
        </div>
      </div>

      <ResponsiveContainer>
        <BarChart
          data={winRateBySymbol}
          layout="vertical"
          margin={{ top: 10, right: 80, bottom: 20, left: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, 100]}
            tickFormatter={(value) => `${value}%`}
            stroke="#666"
            fontSize={12}
            ticks={[0, 25, 50, 75, 100]}
          />
          <YAxis
            type="category"
            dataKey="symbol"
            stroke="#666"
            fontSize={12}
            width={50}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={50} stroke="#f59e0b" strokeWidth={1} strokeDasharray="5 5" />
          <ReferenceLine x={70} stroke="#22c55e" strokeWidth={1} strokeDasharray="5 5" />

          <Bar dataKey="winRate" radius={[0, 4, 4, 0]}>
            {winRateBySymbol.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getWinRateColor(entry.winRate)}
              />
            ))}
            <LabelList
              dataKey="winRate"
              position="right"
              content={({ x, y, width, value, index }) => {
                const data = winRateBySymbol[index];
                if (!data) return null;
                return (
                  <text
                    x={x + width + 5}
                    y={y}
                    dy={4}
                    fontSize={12}
                    fill="#333"
                  >
                    {value.toFixed(0)}% ({data.trades})
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        marginTop: '12px',
        fontSize: '12px',
        color: '#666'
      }}>
        <span><span style={{ color: '#ef4444' }}>●</span> &lt;50% Poor</span>
        <span><span style={{ color: '#f59e0b' }}>●</span> 50-70% Average</span>
        <span><span style={{ color: '#22c55e' }}>●</span> &gt;70% Good</span>
      </div>
    </div>
  );
};

export default WinRateChart;
